import { Component, OnInit, HostListener, NgZone } from '@angular/core';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { faArrowLeft, faPlus, faSpinner, faBolt, faCalendar, faClock, faFileCode, faFolderOpen, faWandMagicSparkles, faUpload, faDownload, faSliders, faBookOpen, faTrash, faFileArrowDown } from '@fortawesome/free-solid-svg-icons';

import { ProcessingsService, Processing, ProcessingOperation, ProcessingExecution, AssistSuggestion, Catalog } from '../../services/processings.service';
import { ModelsService, Model } from '../../services/models.service';
import { ConfigurationsService, Configuration } from '../../services/configurations.service';
import { ArchivesService, Archive } from '../../services/archives.service';
import { UsersService, UserPreferences, DEFAULT_USER_PREFERENCES } from '../../services/users.service';
import { AlertService } from '../../components/alert-service/alert-service';
import { TenantService } from '../../services/tenant.service';
import { TabsComponent, TabItem } from '../../components/tabs/tabs.component';
import { ModalConfirmComponent } from '../../components/modal-confirm/modal-confirm.component';
import { ModalInteractionArchivesAddComponent } from '../../components/modal-interaction-archives-add/modal-interaction-archives-add.component';
import { ModalExecuteProcessingComponent, ProcessingExecuteOptions } from '../../components/modal-execute-processing/modal-execute-processing.component';
import { ProcessingOperationsComponent } from '../../components/processing-operations/processing-operations.component';
import { ProcessingAssistantComponent } from '../../components/processing-assistant/processing-assistant.component';
import { ProcessingCatalogComponent } from '../../components/processing-catalog/processing-catalog.component';
import { ModalTestOperationComponent } from '../../components/modal-test-operation/modal-test-operation.component';
import { CodeViewerComponent } from '../../components/code-viewer/code-viewer.component';
import { SimulationExecutionsListComponent } from '../../components/simulation-executions-list/simulation-executions-list.component';
import { CanComponentDeactivate } from '../../interfaces/can-component-deactivate.interface';
import { AssistantTriggerComponent } from '../../components/assistant-trigger/assistant-trigger.component';
import { SimulationFormShellComponent } from '../../components/simulation-form-shell/simulation-form-shell.component';
import { SimulationModelActionsComponent } from '../../components/simulation-model-actions/simulation-model-actions.component';
import { SimulationModelIoComponent } from '../../components/simulation-model-io/simulation-model-io.component';

type ProcessingForm = Omit<Processing, 'id' | 'created_at' | 'updated_at' | 'tag'>;

@Component({
  selector: 'app-processings-form',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, TranslateModule, NgSelectModule, TabsComponent, ModalConfirmComponent, ModalInteractionArchivesAddComponent, ModalExecuteProcessingComponent, ProcessingOperationsComponent, ProcessingAssistantComponent, ProcessingCatalogComponent, ModalTestOperationComponent, CodeViewerComponent, SimulationExecutionsListComponent, AssistantTriggerComponent, SimulationFormShellComponent, SimulationModelActionsComponent, SimulationModelIoComponent],
  templateUrl: './processings-form.component.html',
  styleUrls: ['./processings-form.component.scss'],
})
export class ProcessingsFormComponent implements OnInit, CanComponentDeactivate {
  icons = { faArrowLeft, faPlus, faSpinner, faBolt, faCalendar, faClock, faFileCode, faFolderOpen, faWandMagicSparkles, faUpload, faDownload, faTrash, faFileArrowDown };

  isEditing = false;
  saving = false;
  loading = false;

  mainTabs: TabItem[] = [
    { id: 'config', label: 'PROCESSINGS.TABS.CONFIG' },
    { id: 'result', label: 'INTERACTIONS.TABS.RESULTS' },
  ];
  activeTab = 'config';

  operationTabs: TabItem[] = [
    { id: 'operations', label: 'PROCESSINGS.TABS.OPERATIONS', icon: faSliders },
    { id: 'catalog', label: 'PROCESSINGS.TABS.CATALOG', icon: faBookOpen },
  ];
  activeOperationTab = 'operations';

  catalogData: Catalog | null = null;
  catalogError: string | null = null;

  executing = false;
  executions: ProcessingExecution[] = [];
  selectedExecutionId: string | null = null;
  currentResult: any = null;

  jsonArchives: Archive[] = [];
  jsonArchivesLoaded = false;
  llmModelOptions: Configuration[] = [];
  delfosServerConfigs: Configuration[] = [];

  showFileManager = false;
  showExecuteModal = false;
  showAssistant = false;

  showTestModal = false;
  testOperationIndex: number | null = null;

  processingModels: { id: string; name: string }[] = [];
  selectedModelId: string | null = null;
  newModelName = '';
  private execConfig: ProcessingExecuteOptions | null = null;

  testLlmConfigId: string | null = null;
  private testServerConfigId: string | null = null;

  activePreviewId: string | null = null;
  activePreviewContent: any = null;
  activePreviewError: string | null = null;

  private jsonCache = new Map<string, any>();
  private pristineSnapshot = '';
  showLeaveModal = false;
  private leaveSubject: Subject<boolean> | null = null;

  form: ProcessingForm = {
    name: '',
    input_archive_ids: [],
    operations: [],
    delfos_server_config_id: null,
    llm_model: null,
  };

  constructor(
    public route: ActivatedRoute,
    private router: Router,
    private service: ProcessingsService,
    private modelsService: ModelsService,
    private configurationsService: ConfigurationsService,
    private archivesService: ArchivesService,
    private usersService: UsersService,
    private alert: AlertService,
    private translate: TranslateService,
    private tenantService: TenantService,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.loadOptions();
    this.loadCatalog();
    this.activeTab = this.route.snapshot.queryParamMap.get('tab') === 'result' ? 'result' : 'config';
    this.loadJsonArchives();
    this.loadProcessingModels();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.loading = true;
      this.service.getById(id).subscribe({
        next: p => {
          if (p) {
            this.form = {
              name: p.name,
              input_archive_ids: [...(p.input_archive_ids || [])],
              operations: JSON.parse(JSON.stringify(p.operations || [])),
              delfos_server_config_id: p.delfos_server_config_id ?? null,
              llm_model: p.llm_model ?? null,
              model_id: p.model_id ?? null,
            };
            this.selectedModelId = p.model_id ?? null;
          }
          this.pristineSnapshot = JSON.stringify(this.form);
          this.syncPreviewSelection();
          this.loading = false;
        },
        error: () => {
          this.alert.showError(this.translate.instant('PROCESSINGS.ERRORS.LOAD_FAILED'));
          this.loading = false;
        },
      });
      this.loadExecutions(id);
    } else {
      const state = history.state as { processingName?: string; processingModelId?: string };
      if (state?.processingName) {
        this.form.name = state.processingName;
      }
      if (state?.processingModelId) {
        this.selectedModelId = state.processingModelId;
        this.onSelectModel(state.processingModelId);
      }
      this.pristineSnapshot = JSON.stringify(this.form);
    }
  }

  initialExecOptions: Partial<ProcessingExecuteOptions> | null = null;

  private loadOptions(): void {
    this.configurationsService.listAllowedForMe().subscribe({
      next: res => {
        const configurations = res.configurations || [];
        this.llmModelOptions = configurations.filter(c => c.type === 'delfos_llm');
        this.delfosServerConfigs = configurations.filter(c => c.type === 'delfos_server');
      },
      error: () => {
        this.llmModelOptions = [];
        this.delfosServerConfigs = [];
      },
    });

    this.usersService.getMe().subscribe({
      next: (response) => {
        const prefs = response.user?.preferences;
        if (!prefs) return;
        this.initialExecOptions = {
          delfos_llm_config_id: prefs.default_delfos_llm_config_id || null,
          delfos_server_config_id: prefs.default_delfos_server_config_id || null,
          ai_model_language: prefs.ai_model_language || 'pt',
        };
        this.testLlmConfigId = prefs.default_delfos_llm_config_id || null;
        this.testServerConfigId = prefs.default_delfos_server_config_id || null;
      },
      error: () => {},
    });
  }

  private loadJsonArchives(): void {
    this.archivesService.list(undefined, undefined, 1, 1000, 'json').subscribe({
      next: res => {
        this.jsonArchives = res.archives || [];
        this.jsonArchivesLoaded = true;
      },
      error: () => { this.jsonArchives = []; this.jsonArchivesLoaded = false; },
    });
  }

  onOpenFileManager(): void {
    this.showFileManager = true;
  }

  onCloseFileManager(): void {
    this.showFileManager = false;
  }

  onFilesConfirmed(ids: string[]): void {
    this.showFileManager = false;
    this.form.input_archive_ids = [...ids];
    this.loadJsonArchives();
    this.syncPreviewSelection();
    this.persistInputArchives();
  }

  get missingArchiveIds(): string[] {
    if (!this.jsonArchivesLoaded) {
      return [];
    }
    const known = new Set(this.jsonArchives.map(a => String(a.id)));
    return (this.form.input_archive_ids || []).filter(id => !known.has(String(id)));
  }

  downloadArchive(archiveId: string, event?: Event): void {
    event?.stopPropagation();
    if (!archiveId) {
      return;
    }
    const filename = this.archiveName(archiveId) || `archive-${archiveId}`;
    this.archivesService.download(archiveId).subscribe({
      next: blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      },
      error: () => {
        this.alert.showError(this.translate.instant('PROCESSINGS.ERRORS.DOWNLOAD_FAILED'));
      },
    });
  }

  removeArchiveReference(archiveId: string | null): void {
    if (!archiveId) {
      return;
    }
    this.form.input_archive_ids = (this.form.input_archive_ids || [])
      .filter(id => String(id) !== String(archiveId));
    if (String(this.activePreviewId) === String(archiveId)) {
      this.activePreviewId = null;
      this.activePreviewContent = null;
      this.activePreviewError = null;
    }
    this.loadJsonArchives();
    this.syncPreviewSelection();
    this.persistInputArchives();
  }

  private persistInputArchives(): void {
    const procId = this.route.snapshot.paramMap.get('id');
    if (!procId) {
      return;
    }
    this.service.update(procId, this.form).subscribe({
      next: () => {
        this.pristineSnapshot = JSON.stringify(this.form);
        this.alert.showSuccess(this.translate.instant('PROCESSINGS.SUCCESS.UPDATED'));
      },
      error: () => { this.alert.showError(this.translate.instant('PROCESSINGS.ERRORS.SAVE_FAILED')); },
    });
  }

  private loadExecutions(id: string): void {
    this.service.getExecutions(id).subscribe({
      next: execs => {
        this.executions = execs || [];
        if (this.executions.length > 0) {
          this.onSelectExecution(this.executions[0].id);
        }
      },
      error: () => { this.executions = []; },
    });
  }

  archiveName(archiveId: string | null): string {
    if (!archiveId) {
      return '';
    }
    const found = this.jsonArchives.find(a => String(a.id) === String(archiveId));
    return found ? found.name : archiveId;
  }

  private syncPreviewSelection(): void {
    const ids = this.form.input_archive_ids || [];
    if (!ids.length) {
      this.activePreviewId = null;
      this.activePreviewContent = null;
      this.activePreviewError = null;
      return;
    }
    if (!this.activePreviewId || !ids.includes(this.activePreviewId)) {
      this.onPreviewChange(ids[0]);
    }
  }

  onPreviewChange(archiveId: string | null): void {
    this.activePreviewId = archiveId;
    this.activePreviewContent = null;
    this.activePreviewError = null;
    if (!archiveId) return;
    this.getJsonContent(archiveId)
      .then(content => { this.activePreviewContent = content; })
      .catch(() => { this.activePreviewError = this.translate.instant('PROCESSINGS.ERRORS.INVALID_JSON'); });
  }

  private async getJsonContent(archiveId: string): Promise<any> {
    if (this.jsonCache.has(archiveId)) {
      return this.jsonCache.get(archiveId);
    }
    const blob = await firstValueFrom(this.archivesService.download(archiveId));
    const text = await blob.text();
    const parsed = JSON.parse(text);
    this.jsonCache.set(archiveId, parsed);
    return parsed;
  }

  get previewJson(): string {
    if (this.activePreviewError) return this.activePreviewError;
    if (this.activePreviewContent === null) return '';
    return JSON.stringify(this.activePreviewContent, null, 2);
  }

  private resolveLlmKey(configId: string | null | undefined): string | null {
    if (!configId) return null;
    const cfg = this.llmModelOptions.find(c => c.id === configId);
    return cfg ? cfg.key : null;
  }

  private resolveLlmModel(): string | null {
    return this.resolveLlmKey(this.execConfig?.delfos_llm_config_id);
  }

  get execConfigServerId(): string | null {
    return this.execConfig?.delfos_server_config_id ?? null;
  }

  get execConfigLlm(): string | null {
    return this.resolveLlmModel();
  }

  get testServerId(): string | null {
    return this.testServerConfigId;
  }

  get testLlmKey(): string | null {
    return this.resolveLlmKey(this.testLlmConfigId);
  }

  get testOperation(): ProcessingOperation | null {
    return this.testOperationIndex === null ? null : (this.form.operations[this.testOperationIndex] ?? null);
  }

  onTestRequested(index: number): void {
    this.testOperationIndex = index;
    this.showTestModal = true;
  }

  testCurrentOperation = async (): Promise<{ output?: any; error?: string }> => {
    if (this.testOperationIndex === null) {
      return { error: this.translate.instant('PROCESSINGS.ERRORS.NO_INPUT') };
    }
    return this.testRunner(this.testOperationIndex);
  };

  testRunner = async (index: number): Promise<{ output?: any; error?: string }> => {
    if (this.activePreviewContent === null) {
      return { error: this.translate.instant('PROCESSINGS.ERRORS.NO_INPUT') };
    }
    const operation = this.form.operations[index];
    if (!operation) {
      return { error: this.translate.instant('PROCESSINGS.ERRORS.NO_INPUT') };
    }
    try {
      const res = await firstValueFrom(this.service.testOperation({
        input: this.activePreviewContent,
        operation,
        preceding: this.form.operations.slice(0, index).filter(op => op.scope === 'document'),
        delfos_server_config_id: this.testServerConfigId,
        llm_model: this.resolveLlmKey(this.testLlmConfigId),
      }));
      if (res?.error) {
        return { error: res.error };
      }
      return { output: res?.output };
    } catch (e: any) {
      return { error: e?.error?.detail || e?.message || String(e) };
    }
  };

  private loadProcessingModels(): void {
    this.modelsService.list(undefined, undefined, 1, 9999).subscribe({
      next: res => {
        this.processingModels = (res.models || [])
          .filter(m => m['model_type'] === 'processing')
          .map(m => ({ id: String(m.id), name: m.name }));
      },
      error: () => { this.processingModels = []; },
    });
  }

  onSelectModel(id: string | null): void {
    if (!id) return;
    this.modelsService.getById(id).subscribe({
      next: m => {
        if (m) {
          this.form.operations = JSON.parse(JSON.stringify(m.operations || []));
          this.form.model_id = id;
          this.selectedModelId = id;
          this.newModelName = m.name;

          const procId = this.route.snapshot.paramMap.get('id');
          if (procId) {
            this.service.update(procId, this.form).subscribe({
              next: () => { this.pristineSnapshot = JSON.stringify(this.form); },
              error: () => {},
            });
          }

          this.alert.showSuccess(this.translate.instant('PROCESSINGS.MODEL.LOADED'));
        }
      },
      error: () => { this.alert.showError(this.translate.instant('PROCESSINGS.ERRORS.LOAD_FAILED')); },
    });
  }

  isModelNameTaken(name: string): boolean {
    const target = name.trim().toLowerCase();
    return this.processingModels.some(m => (m.name || '').trim().toLowerCase() === target);
  }

  onSaveModel(): void {
    if (this.saving) return;
    const modelName = this.newModelName?.trim();
    if (!modelName) {
      this.alert.showError(this.translate.instant('PROCESSINGS.MODEL.NAME_REQUIRED'));
      return;
    }
    if (this.isModelNameTaken(modelName)) {
      this.alert.showError(this.translate.instant('PROCESSINGS.MODEL.DUPLICATE_NAME'));
      return;
    }

    this.saving = true;
    const modelPayload: Model = {
      name: modelName,
      model_type: 'processing',
      operations: this.form.operations,
      user_list: [],
      general_rules: [],
      general_questions: [],
      references: [],
      version: '1',
    };

    this.modelsService.create(modelPayload).subscribe({
      next: (modelId: string) => {
        this.selectedModelId = modelId;
        this.form.model_id = modelId;
        this.loadProcessingModels();
        this.persistSimulation(() => {
          this.alert.showSuccess(this.translate.instant('PROCESSINGS.MODEL.SAVED'));
          this.saving = false;
        });
      },
      error: (err) => {
        this.alert.showError(err);
        this.saving = false;
      },
    });
  }

  private persistSimulation(onDone: () => void): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.service.update(id, this.form).subscribe({
        next: () => { this.pristineSnapshot = JSON.stringify(this.form); onDone(); },
        error: () => { this.alert.showError(this.translate.instant('PROCESSINGS.ERRORS.SAVE_FAILED')); this.saving = false; },
      });
    } else {
      this.service.create(this.form).subscribe({
        next: (newId) => {
          this.pristineSnapshot = JSON.stringify(this.form);
          onDone();
          this.router.navigate(this.tenantService.buildRoute(`/processings/${newId}`));
        },
        error: () => { this.alert.showError(this.translate.instant('PROCESSINGS.ERRORS.SAVE_FAILED')); this.saving = false; },
      });
    }
  }

  onImportOperations(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        this.ngZone.run(() => {
          try {
            const parsed = JSON.parse(reader.result as string);
            const ops = Array.isArray(parsed) ? parsed : (parsed.operations || []);
            this.form.operations = ops;
            this.alert.showSuccess(this.translate.instant('PROCESSINGS.IMPORT.SUCCESS'));
          } catch {
            this.alert.showError(this.translate.instant('PROCESSINGS.IMPORT.ERROR'));
          }
        });
      };
      reader.readAsText(file);
    };
    input.click();
  }

  onExportOperations(): void {
    const data = { name: this.form.name, operations: this.form.operations };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(this.form.name || 'processing').trim()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  onOperationsChange(operations: ProcessingOperation[]): void {
    this.form.operations = operations;
  }

  private loadCatalog(): void {
    this.catalogError = null;
    this.service.catalog().subscribe({
      next: catalog => { this.catalogData = catalog; this.catalogError = null; },
      error: err => { this.catalogData = null; this.catalogError = this.failureDetail(err); },
    });
  }

  private failureDetail(err: any): string {
    return err?.error?.detail || err?.message || this.translate.instant('PROCESSINGS.CATALOG.RETRY');
  }

  onCatalogRetry(): void {
    this.loadCatalog();
  }

  onAddSuggestion(suggestion: AssistSuggestion): void {
    const operation: ProcessingOperation = {
      id: this.newOperationId(),
      description: suggestion.description || '',
      type: suggestion.type === 'ai' ? 'ai' : 'jsonata',
      expression: suggestion.expression || '',
      instruction: suggestion.instruction || '',
    };
    this.form.operations = [...this.form.operations, operation];
  }

  private newOperationId(): string {
    return 'op_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  }

  get canSave(): boolean {
    return !!this.form.name?.trim();
  }

  get isDirty(): boolean {
    return this.pristineSnapshot !== '' && JSON.stringify(this.form) !== this.pristineSnapshot;
  }

  goBack(): void {
    this.router.navigate(this.tenantService.buildRoute('/simulations'));
  }

  save(): void {
    if (!this.canSave) return;
    this.saving = true;
    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.service.update(id, this.form).subscribe({
        next: () => {
          this.alert.showSuccess(this.translate.instant('PROCESSINGS.SUCCESS.UPDATED'));
          this.saving = false;
          this.pristineSnapshot = JSON.stringify(this.form);
        },
        error: () => {
          this.alert.showError(this.translate.instant('PROCESSINGS.ERRORS.SAVE_FAILED'));
          this.saving = false;
        },
      });
    } else {
      this.service.create(this.form).subscribe({
        next: (newId) => {
          this.alert.showSuccess(this.translate.instant('PROCESSINGS.SUCCESS.CREATED'));
          this.saving = false;
          this.pristineSnapshot = JSON.stringify(this.form);
          this.router.navigate(this.tenantService.buildRoute(`/processings/${newId}`));
        },
        error: () => {
          this.alert.showError(this.translate.instant('PROCESSINGS.ERRORS.SAVE_FAILED'));
          this.saving = false;
        },
      });
    }
  }

  execute(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id || this.executing) return;

    if (this.isDirty) {
      this.alert.showWarning(this.translate.instant('PROCESSINGS.EXECUTE.SAVE_FIRST'));
      return;
    }
    if (!this.form.input_archive_ids.length || !this.form.operations.length) {
      this.alert.showWarning(this.translate.instant('PROCESSINGS.EXECUTE.MISSING_INPUTS'));
      return;
    }

    this.showExecuteModal = true;
  }

  onCloseExecuteModal(): void {
    this.showExecuteModal = false;
  }

  async onConfirmExecute(opts: ProcessingExecuteOptions): Promise<void> {
    this.showExecuteModal = false;
    this.execConfig = opts;

    const id = this.route.snapshot.paramMap.get('id');
    if (!id) return;

    if (opts.save_as_default) {
      this.persistDefaults(opts);
    }

    this.executing = true;
    this.activeTab = 'result';

    const result: Record<string, any> = {};
    const inputDataByFile: Record<string, any> = {};
    const stepsByFile: Record<string, any> = {};
    const buildConfig = (status: string) => ({
      inputs: this.form.input_archive_ids.map(aid => this.archiveName(aid)),
      input_data: inputDataByFile,
      operations: this.form.operations,
      llm_model: this.resolveLlmModel(),
      ai_model_language: opts.ai_model_language,
      steps: stepsByFile,
      status,
    });

    let execution: ProcessingExecution;
    try {
      execution = await firstValueFrom(this.service.saveExecution(id, { result, config: buildConfig('running') }));
    } catch (e: any) {
      this.executing = false;
      this.alert.showError(this.executeErrorMessage(e));
      return;
    }
    this.executions = [execution, ...this.executions];
    this.selectedExecutionId = execution.id;

    let hadError = false;
    try {
      for (const archiveId of this.form.input_archive_ids) {
        const archiveName = this.archiveName(archiveId);
        try {
          const input = await this.getJsonContent(archiveId);
          const run = await firstValueFrom(this.service.run({
            input,
            operations: this.form.operations,
            delfos_server_config_id: this.execConfigServerId,
            llm_model: this.resolveLlmModel(),
          }));
          inputDataByFile[archiveName] = input;
          result[archiveName] = run.result;
          stepsByFile[archiveName] = run.metadata?.steps || [];
        } catch (fileError: any) {
          hadError = true;
          result[archiveName] = null;
          stepsByFile[archiveName] = [{ status: 'error', error: this.executeErrorMessage(fileError) }];
        }

        const updated = await firstValueFrom(this.service.updateExecution(id, execution.id, {
          result,
          config: buildConfig('running'),
        }));
        this.replaceExecution(updated);
      }

      const finalExecution = await firstValueFrom(this.service.updateExecution(id, execution.id, {
        result,
        config: buildConfig(hadError ? 'completed_with_errors' : 'completed'),
      }));
      this.replaceExecution(finalExecution);

      if (hadError) {
        this.alert.showWarning(this.translate.instant('PROCESSINGS.EXECUTE.PARTIAL_SUCCESS'));
      } else {
        this.alert.showSuccess(this.translate.instant('PROCESSINGS.EXECUTE.SUCCESS'));
      }
    } catch (e: any) {
      this.alert.showError(this.executeErrorMessage(e));
    } finally {
      this.executing = false;
    }
  }

  private replaceExecution(execution: ProcessingExecution): void {
    const index = this.executions.findIndex(e => e.id === execution.id);
    if (index === -1) return;
    const next = this.executions.slice();
    next[index] = execution;
    this.executions = next;
  }

  private executeErrorMessage(e: any): string {
    const backendMessage = e?.error?.detail?.message || (typeof e?.error?.detail === 'string' ? e.error.detail : null);
    return backendMessage || e?.message || this.translate.instant('PROCESSINGS.EXECUTE.FAILED');
  }

  private persistDefaults(opts: ProcessingExecuteOptions): void {
    this.usersService.getMe().subscribe({
      next: (response) => {
        const current: UserPreferences = { ...DEFAULT_USER_PREFERENCES, ...(response.user?.preferences || {}) };
        const merged: UserPreferences = {
          ...current,
          default_delfos_llm_config_id: opts.delfos_llm_config_id || current.default_delfos_llm_config_id,
          default_delfos_server_config_id: opts.delfos_server_config_id || current.default_delfos_server_config_id,
          ai_model_language: opts.ai_model_language,
        };
        this.usersService.updatePreferences(merged).subscribe();
      },
    });
  }

  onSelectExecution(executionId: string): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id || !executionId) return;
    this.selectedExecutionId = executionId;
    this.service.getExecution(id, executionId).subscribe({
      next: (execution) => { this.currentResult = execution.result; },
      error: () => {},
    });
  }

  resultNumber(execution: ProcessingExecution): number {
    const idx = this.executions.findIndex(e => e.id === execution.id);
    return idx === -1 ? this.executions.length : this.executions.length - idx;
  }

  executionLabel(execution: ProcessingExecution): string {
    return this.translate.instant('PROCESSINGS.EXECUTE.RESULT_LABEL', { number: this.resultNumber(execution) });
  }

  get selectedExecution(): ProcessingExecution | null {
    return this.executions.find(e => e.id === this.selectedExecutionId) || null;
  }

  get resultJson(): string {
    if (this.currentResult === null || this.currentResult === undefined) return '';
    return JSON.stringify(this.currentResult, null, 2);
  }

  canDeactivate(): boolean | Observable<boolean> {
    if (!this.isDirty) {
      return true;
    }
    this.leaveSubject = new Subject<boolean>();
    this.showLeaveModal = true;
    return this.leaveSubject.asObservable();
  }

  onLeaveConfirm(): void {
    this.showLeaveModal = false;
    this.leaveSubject?.next(true);
    this.leaveSubject?.complete();
    this.leaveSubject = null;
  }

  onLeaveCancel(): void {
    this.showLeaveModal = false;
    this.leaveSubject?.next(false);
    this.leaveSubject?.complete();
    this.leaveSubject = null;
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: BeforeUnloadEvent): void {
    if (this.isDirty) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
}
